// `REVENUE_KPI_MEASURES` is the canonical measure list for the revenue KPI
// query, consumed by both the API/CLI (`buildRevenueKpiQueryForRange`) and the
// UI (`MetricsMapping.revenue_kpi_table.q` imports it).
// `BRAND_VALUE_BREAKDOWN_MEASURES` mirrors the inline `brandValueQuery` in
// `apps/ui-rtk/src/pages/Revenue/KPI/connect.ts` — keep that one aligned until
// the UI imports it too.

import type { AggregationGranularity } from './types';

interface DateRange {
  currentStart: string;
  currentEnd: string;
  comparisonStart: string;
  comparisonEnd: string;
}

function toIso(d: Date): string {
  return d.toISOString().split('T')[0];
}

export function buildRevenueKpiDateRange(granularity: AggregationGranularity, now: Date = new Date()): DateRange {
  let currentStart: Date;
  let currentEnd: Date;
  let comparisonStart: Date;
  let comparisonEnd: Date;

  if (granularity === 'day') {
    // Daily: last 60 days ending yesterday; comparison is the SAME 60 calendar
    // days one year earlier — mirrors the UI's revenue_kpi_table window (the
    // dashboard's YoY merge pairs daily buckets by month+day, so both surfaces
    // must compare against the prior YEAR, not the prior 60-day period).
    currentEnd = new Date(now);
    currentEnd.setDate(currentEnd.getDate() - 1);
    currentStart = new Date(currentEnd);
    currentStart.setDate(currentStart.getDate() - 59);
    comparisonStart = new Date(currentStart);
    comparisonStart.setFullYear(comparisonStart.getFullYear() - 1);
    comparisonEnd = new Date(currentEnd);
    comparisonEnd.setFullYear(comparisonEnd.getFullYear() - 1);
  } else if (granularity === 'month') {
    currentEnd = now;
    currentStart = new Date(now.getFullYear(), now.getMonth() - 12, 1);
    comparisonStart = new Date(now.getFullYear() - 2, now.getMonth(), 1);
    comparisonEnd = new Date(now.getFullYear() - 1, now.getMonth() + 1, 0);
  } else {
    currentEnd = now;
    currentStart = new Date(now);
    currentStart.setDate(currentStart.getDate() - 52 * 7);
    comparisonStart = new Date(currentStart);
    comparisonStart.setDate(comparisonStart.getDate() - 52 * 7);
    comparisonEnd = new Date(currentStart);
  }

  return {
    currentStart: toIso(currentStart),
    currentEnd: toIso(currentEnd),
    comparisonStart: toIso(comparisonStart),
    comparisonEnd: toIso(comparisonEnd),
  };
}

export function buildBrandValueBreakdownDateRange(granularity: AggregationGranularity, now: Date = new Date()): DateRange {
  if (granularity === 'month') {
    const currentEnd = now;
    const currentStart = new Date(now.getFullYear(), now.getMonth() - 12, 1);
    const comparisonStart = new Date(now.getFullYear() - 2, now.getMonth(), 1);
    const comparisonEnd = new Date(now.getFullYear() - 1, now.getMonth() + 1, 0);
    return {
      currentStart: toIso(currentStart),
      currentEnd: toIso(currentEnd),
      comparisonStart: toIso(comparisonStart),
      comparisonEnd: toIso(comparisonEnd),
    };
  }

  const currentEnd = now;
  const currentStart = new Date(now);
  currentStart.setDate(currentStart.getDate() - 52 * 7);
  const comparisonStart = new Date(currentStart);
  comparisonStart.setDate(comparisonStart.getDate() - 52 * 7);
  const comparisonEnd = new Date(currentStart);

  return {
    currentStart: toIso(currentStart),
    currentEnd: toIso(currentEnd),
    comparisonStart: toIso(comparisonStart),
    comparisonEnd: toIso(comparisonEnd),
  };
}

export const REVENUE_KPI_MEASURES: readonly string[] = [
  'metrics.instagram_ads_spend',
  'metrics.facebook_ads_spend',
  'metrics.tiktok_ads_spend',
  'metrics.pinterest_ads_spend',
  'metrics.google_ads_total_spend',
  'metrics.amazon_ads_total_spend',
  'metrics.shopify_baseline_revenue',
  'metrics.ga_baseline_revenue',
  'metrics.ga_multi_property_baseline_revenue',
  'metrics.shopify_triggered_revenue',
  'metrics.ga_triggered_revenue',
  'metrics.ga_multi_property_triggered_revenue',
  'metrics.shopify_paid_revenue',
  'metrics.ga_paid_revenue',
  'metrics.ga_multi_property_paid_revenue',
  'metrics.amazon_total_revenue',
  'metrics.amazon_paid_revenue',
  'metrics.amazon_owned_revenue',
  'metrics.shopify_total_new_customer_revenue',
  'metrics.shopify_total_repeat_customer_revenue',
  'metrics.shopify_ecomm_new_customer_revenue',
  'metrics.shopify_ecomm_repeat_customer_revenue',
  'metrics.shopify_ecomm_total_customer_revenue',
  'metrics.shopify_dtc_retail_new_customer_revenue',
  'metrics.shopify_dtc_retail_repeat_customer_revenue',
  'metrics.shopify_dtc_retail_total_customer_revenue',
  'metrics.shopify_third_party_ecomm_new_customer_revenue',
  'metrics.shopify_third_party_ecomm_repeat_customer_revenue',
  'metrics.shopify_third_party_ecomm_total_customer_revenue',
  'metrics.shopify_subscription_new_customer_revenue',
  'metrics.shopify_subscription_repeat_customer_revenue',
  'metrics.shopify_subscription_total_customer_revenue',
  'metrics.ga_total_revenue',
  'metrics.ga_multi_property_revenue',
  'metrics.shopify_total_traffic_source_revenue',
  'metrics.shopify_total_customer_revenue',
  'metrics.wholesale_rei_regular_revenue',
  'metrics.wholesale_sams_club_regular_revenue',
  'metrics.wholesale_whole_foods_regular_revenue',
  'metrics.wholesale_walmart_regular_revenue',
  'metrics.wholesale_target_regular_revenue',
  'metrics.wholesale_spins_regular_revenue',
  'metrics.wholesale_kroger_regular_revenue',
  'metrics.wholesale_total_revenue',
  'metrics.shopify_total_gross_profit',
  'metrics.shopify_ecomm_gross_profit',
  'metrics.shopify_subscription_gross_profit',
  'metrics.shopify_dtc_retail_gross_profit',
  'metrics.amazon_gross_profit',
  'metrics.total_contribution_margin',
  'metrics.subscription_contribution_margin',
  'metrics.dtc_retail_contribution_margin',
  'metrics.shopify_total_contribution_margin',
  'metrics.amazon_contribution_margin',
  'metrics.total_business_contribution_margin',
  'metrics.contribution_margin_percent',
  'metrics.organic_per_session_value',
  'metrics.baseline_per_session_value',
  'metrics.triggered_per_session_value',
  'metrics.paid_per_session_value',
  'metrics.total_per_session_value',
  'metrics.google_search_organic_branded_search_impressions',
  'metrics.total_google_search_clicks',
  'metrics.branded_search_conversion_rate',
  'metrics.spend_percentage_of_new_customer_revenue',
  'metrics.amer',
  'metrics.d30_brand_value',
  'metrics.d180_brand_value',
  'metrics.total_ad_spend',
  'metrics.ga_paid_sessions',
  'metrics.ga_triggered_sessions',
  'metrics.ga_baseline_sessions',
  'metrics.ga_sessions_from_organic_sessions',
  'metrics.ga_sessions',
  'metrics.ga_total_homepage_viewers',
  'metrics.ga_paid_homepage_viewers',
  'metrics.ga_organic_homepage_viewers',
  'metrics.ga_triggered_homepage_viewers',
  'metrics.ga_total_collection_viewers',
  'metrics.ga_paid_collection_viewers',
  'metrics.ga_organic_collection_viewers',
  'metrics.ga_triggered_collection_viewers',
  'metrics.ga_total_product_page_viewers',
  'metrics.ga_paid_product_page_viewers',
  'metrics.ga_organic_product_page_viewers',
  'metrics.ga_triggered_product_page_viewers',
  'metrics.google_ads_paid_branded_search_clicks',
  'metrics.google_ads_paid_branded_search_revenue',
  'metrics.google_ads_paid_branded_search_revenue_per_click',
  'metrics.ga_revenue_from_organic_sessions',
  'metrics.ga_sessions_from_organic_plus_paid_branded_search_sessions',
  'metrics.ga_sessions_from_organic_plus_paid_branded_search_revenue',
  'metrics.ga_sessions_from_organic_plus_paid_branded_search_revenue_per_session',
  'metrics.shopify_baseline_aov',
  'metrics.shopify_triggered_aov',
  'metrics.shopify_paid_aov',
  'metrics.shopify_total_aov',
  'metrics.shopify_subscription_revenue',
  'metrics.dtc_ecomm_mer',
  'metrics.dtc_ecomm_amer',
  'metrics.total_dtc_mer',
  'metrics.total_dtc_amer',
  'metrics.total_business_mer',
  'metrics.dtc_ecomm_plus_amazon_mer',
  'metrics.facebook_ads_daily_reach',
  'metrics.facebook_ads_weekly_reach',
  'metrics.facebook_ads_monthly_reach',
  'metrics.facebook_ads_daily_impressions',
  'metrics.facebook_ads_daily_reach_prospecting',
  'metrics.facebook_ads_weekly_reach_prospecting',
  'metrics.facebook_ads_monthly_reach_prospecting',
  'metrics.facebook_ads_daily_impressions_prospecting',
  'metrics.facebook_ads_daily_spend_prospecting',
  'metrics.facebook_ads_daily_link_clicks_prospecting',
  'metrics.facebook_ads_daily_reach_engaged',
  'metrics.facebook_ads_weekly_reach_engaged',
  'metrics.facebook_ads_monthly_reach_engaged',
  'metrics.facebook_ads_daily_impressions_engaged',
  'metrics.facebook_ads_daily_spend_engaged',
  'metrics.facebook_ads_daily_link_clicks_engaged',
  'metrics.facebook_ads_daily_reach_existing',
  'metrics.facebook_ads_weekly_reach_existing',
  'metrics.facebook_ads_monthly_reach_existing',
  'metrics.facebook_ads_daily_impressions_existing',
  'metrics.facebook_ads_daily_spend_existing',
  'metrics.facebook_ads_daily_link_clicks_existing',
  'metrics.facebook_ads_daily_reach_unknown',
  'metrics.facebook_ads_weekly_reach_unknown',
  'metrics.facebook_ads_monthly_reach_unknown',
  'metrics.facebook_ads_daily_impressions_unknown',
  'metrics.facebook_ads_daily_spend_unknown',
  'metrics.facebook_ads_daily_link_clicks_unknown',
  'metrics.facebook_ads_daily_reach_uncategorized',
  'metrics.facebook_ads_weekly_reach_uncategorized',
  'metrics.facebook_ads_monthly_reach_uncategorized',
  'metrics.facebook_ads_daily_impressions_uncategorized',
  'metrics.facebook_ads_daily_spend_uncategorized',
  'metrics.facebook_ads_daily_link_clicks_uncategorized',
  'metrics.ga_revenue_baseline_plus_paid_brand_search',
  'metrics.ga_amazon_baseline_revenue_plus_paid_brand_search',
  'metrics.shopify_baseline_plus_paid_brand_search_revenue',
  'metrics.shopify_baseline_plus_subscription_plus_paid_brand_search_revenue',
  'metrics.shopify_plus_subscription_amazon_baseline_revenue',
  'metrics.shopify_amazon_baseline_revenue',
  'metrics.total_business_revenue_shopify',
  'metrics.total_business_revenue_ga',
  'metrics.total_likes',
  'metrics.total_comments',
  'metrics.total_shares',
  'metrics.total_saves',
  'metrics.total_new_followers',
  'metrics.total_new_subscribers',
  'metrics.total_followers_plus_subscribers',
  'metrics.youtube_channel_views',
  'metrics.youtube_ads_video_views',
  'metrics.total_platform_engagements',
  'metrics.total_digital_behavior',
  'metrics.total_mentions_count',
  'metrics.total_tags_count',
  'metrics.total_mentioned_likes',
  'metrics.total_tagged_likes',
  // Customer order cohorts (first/2nd/3rd/4th orders) by channel — custom-report catalog
  'metrics.shopify_ecomm_new_customer_orders',
  'metrics.shopify_ecomm_second_customer_orders',
  'metrics.shopify_ecomm_third_customer_orders',
  'metrics.shopify_ecomm_fourth_customer_orders',
  'metrics.shopify_dtc_retail_new_customer_orders',
  'metrics.shopify_dtc_retail_second_customer_orders',
  'metrics.shopify_dtc_retail_third_customer_orders',
  'metrics.shopify_dtc_retail_fourth_customer_orders',
  'metrics.shopify_subscription_new_customer_orders',
  'metrics.shopify_subscription_second_customer_orders',
  'metrics.shopify_subscription_third_customer_orders',
  'metrics.shopify_subscription_fourth_customer_orders',
  'metrics.shopify_third_party_ecomm_new_customer_orders',
  'metrics.shopify_third_party_ecomm_second_customer_orders',
  'metrics.shopify_third_party_ecomm_third_customer_orders',
  'metrics.shopify_third_party_ecomm_fourth_customer_orders',
  'metrics.shopify_third_party_retail_new_customer_orders',
  'metrics.shopify_third_party_retail_second_customer_orders',
  'metrics.shopify_third_party_retail_third_customer_orders',
  'metrics.shopify_third_party_retail_fourth_customer_orders',
  'metrics.shopify_total_new_customer_orders',
  'metrics.shopify_total_second_customer_orders',
  'metrics.shopify_total_third_customer_orders',
  'metrics.shopify_total_fourth_customer_orders',
  // First-order incremental LTV averages by channel & horizon — custom-report catalog
  'metrics.shopify_ecomm_first_order_incremental_30d_ltv',
  'metrics.shopify_ecomm_first_order_incremental_60d_ltv',
  'metrics.shopify_ecomm_first_order_incremental_90d_ltv',
  'metrics.shopify_ecomm_first_order_incremental_180d_ltv',
  'metrics.shopify_ecomm_first_order_incremental_365d_ltv',
  'metrics.shopify_dtc_retail_first_order_incremental_30d_ltv',
  'metrics.shopify_dtc_retail_first_order_incremental_60d_ltv',
  'metrics.shopify_dtc_retail_first_order_incremental_90d_ltv',
  'metrics.shopify_dtc_retail_first_order_incremental_180d_ltv',
  'metrics.shopify_dtc_retail_first_order_incremental_365d_ltv',
  'metrics.shopify_subscription_first_order_incremental_30d_ltv',
  'metrics.shopify_subscription_first_order_incremental_60d_ltv',
  'metrics.shopify_subscription_first_order_incremental_90d_ltv',
  'metrics.shopify_subscription_first_order_incremental_180d_ltv',
  'metrics.shopify_subscription_first_order_incremental_365d_ltv',
  'metrics.shopify_third_party_ecomm_first_order_incremental_30d_ltv',
  'metrics.shopify_third_party_ecomm_first_order_incremental_60d_ltv',
  'metrics.shopify_third_party_ecomm_first_order_incremental_90d_ltv',
  'metrics.shopify_third_party_ecomm_first_order_incremental_180d_ltv',
  'metrics.shopify_third_party_ecomm_first_order_incremental_365d_ltv',
  'metrics.shopify_third_party_retail_first_order_incremental_30d_ltv',
  'metrics.shopify_third_party_retail_first_order_incremental_60d_ltv',
  'metrics.shopify_third_party_retail_first_order_incremental_90d_ltv',
  'metrics.shopify_third_party_retail_first_order_incremental_180d_ltv',
  'metrics.shopify_third_party_retail_first_order_incremental_365d_ltv',
  'metrics.shopify_total_first_order_incremental_30d_ltv',
  'metrics.shopify_total_first_order_incremental_60d_ltv',
  'metrics.shopify_total_first_order_incremental_90d_ltv',
  'metrics.shopify_total_first_order_incremental_180d_ltv',
  'metrics.shopify_total_first_order_incremental_365d_ltv',
  // Metrics Explorer measures exposed to custom reports (catalog-only groups
  // in metric-groups.ts). Brand value detail:
  'metrics.d60_brand_value',
  'metrics.d90_brand_value',
  'metrics.d120_brand_value',
  'metrics.d150_brand_value',
  'metrics.brand_factor',
  'metrics.monetization_factor',
  'metrics.total_brand_roas',
  // Branded search detail
  'metrics.google_ads_paid_branded_search_impressions',
  'metrics.google_search_organic_branded_search_clicks',
  'metrics.google_search_organic_branded_search_impressions_unattributed',
  // GA revenue detail (single-property + multi-property variants)
  'metrics.ga_paid_revenue',
  'metrics.ga_triggered_revenue',
  'metrics.ga_total_revenue_from_all_sessions',
  'metrics.ga_multi_property_total_revenue_from_all_sessions',
  'metrics.ga_multi_property_revenue_baseline_plus_paid_brand_search',
  'metrics.ga_multi_property_amazon_baseline_revenue_plus_paid_brand_search',
  // Subscribers + social ad spend rollup
  'metrics.klaviyo_email_subscribers',
  'metrics.total_social_ad_spend',
  // Facebook engagement (paid ads + organic Pages + combined totals)
  'metrics.facebook_ads_likes',
  'metrics.facebook_ads_comments',
  'metrics.facebook_ads_shares',
  'metrics.facebook_ads_follows',
  'metrics.facebook_pages_likes',
  'metrics.facebook_pages_comments',
  'metrics.facebook_pages_shares',
  'metrics.facebook_pages_follows',
  'metrics.facebook_total_likes',
  'metrics.facebook_total_comments',
  'metrics.facebook_total_shares',
  'metrics.facebook_total_follows',
  'metrics.facebook_total_saves',
  // Instagram engagement (paid ads + organic posts + combined totals)
  'metrics.instagram_ads_likes',
  'metrics.instagram_ads_comments',
  'metrics.instagram_ads_shares',
  'metrics.instagram_ads_saves',
  'metrics.instagram_business_likes',
  'metrics.instagram_business_comments',
  'metrics.instagram_business_shares',
  'metrics.instagram_business_saves',
  'metrics.instagram_total_likes',
  'metrics.instagram_total_comments',
  'metrics.instagram_total_shares',
  'metrics.instagram_total_saves',
  'metrics.instagram_total_follows',
  // TikTok engagement (paid ads + organic videos + combined totals)
  'metrics.tiktok_ads_likes',
  'metrics.tiktok_ads_comments',
  'metrics.tiktok_ads_shares',
  'metrics.tiktok_ads_saves',
  'metrics.tiktok_ads_follows',
  'metrics.tiktok_videos_likes',
  'metrics.tiktok_videos_comments',
  'metrics.tiktok_videos_shares',
  'metrics.tiktok_total_likes',
  'metrics.tiktok_total_comments',
  'metrics.tiktok_total_shares',
  'metrics.tiktok_total_follows',
  // YouTube channel engagement
  'metrics.youtube_channel_likes',
  'metrics.youtube_channel_comments',
  'metrics.youtube_channel_shares',
  'metrics.youtube_channel_saves',
  'metrics.youtube_channel_follows',
];

export const BRAND_VALUE_BREAKDOWN_MEASURES: readonly string[] = [
  'company_platform_brand_value.total_brand_value',
  'company_platform_brand_value.ad_channel_group_brand_value',
  'company_platform_brand_value.organic_channel_group_brand_value',
  'company_platform_brand_value.mentions_channel_group_brand_value',
  'company_platform_brand_value.subscription_channel_group_brand_value',
  'company_platform_brand_value.search_channel_group_brand_value',
  'company_platform_brand_value.unattributed_channel_group_brand_value',
];

// Facebook Ads reach is non-additive, so the Cube model exposes a dedicated
// pre-aggregated measure per grain (daily/weekly/monthly) and consumers remap
// only the grain matching the query granularity onto the synthetic
// `metrics.facebook_ads_reach*` keys (see
// `MetricsMapping.revenue_kpi_table.postProcess`). The other two grains would
// be fetched and immediately discarded, so drop them from the measure list.
const FB_REACH_MEASURE_RE = /^metrics\.facebook_ads_(daily|weekly|monthly)_reach(_|$)/;

const FB_REACH_GRAIN_BY_GRANULARITY: Record<string, string> = {
  day: 'daily',
  week: 'weekly',
  month: 'monthly',
};

export function filterFbReachMeasuresByGranularity(measures: readonly string[], granularity?: string): string[] {
  // Unknown granularities fall back to weekly, matching the remap step.
  const grain = FB_REACH_GRAIN_BY_GRANULARITY[granularity ?? 'week'] ?? 'weekly';
  return measures.filter((measure) => {
    const match = FB_REACH_MEASURE_RE.exec(measure);
    return match === null || match[1] === grain;
  });
}

interface BuildKpiQueryArgs {
  measures: readonly string[];
  timeDimension: string;
  scalerDimension: string;
  dateRange: [string, string];
  granularity?: AggregationGranularity;
  depVarScaler?: string[];
}

/**
 * Common Cube query builder for single-range KPI queries. The two public
 * helpers below just inject the right measure list, time dimension, and
 * scaler dimension for their respective cubes.
 */
function buildKpiQueryForRange(args: BuildKpiQueryArgs) {
  const granularity: AggregationGranularity = args.granularity ?? 'week';
  const depVarScaler = args.depVarScaler ?? [];

  return {
    measures: [...args.measures],
    ...(depVarScaler.length > 0
      ? {
          filters: [
            {
              and: [
                {
                  dimension: args.scalerDimension,
                  operator: 'equals',
                  values: depVarScaler,
                },
              ],
            },
          ],
        }
      : {}),
    timeDimensions: [
      {
        dimension: args.timeDimension,
        granularity,
        dateRange: args.dateRange,
      },
    ],
  };
}

export function buildRevenueKpiQueryForRange(args: {
  dateRange: [string, string];
  granularity?: AggregationGranularity;
  depVarScaler?: string[];
}) {
  return buildKpiQueryForRange({
    measures: filterFbReachMeasuresByGranularity(REVENUE_KPI_MEASURES, args.granularity),
    timeDimension: 'metrics.date',
    scalerDimension: 'metrics.dep_var_scaler',
    ...args,
  });
}

export function buildBrandValueBreakdownQueryForRange(args: {
  dateRange: [string, string];
  granularity?: AggregationGranularity;
  depVarScaler?: string[];
}) {
  return buildKpiQueryForRange({
    measures: BRAND_VALUE_BREAKDOWN_MEASURES,
    timeDimension: 'company_platform_brand_value.engagement_date',
    scalerDimension: 'company_platform_brand_value.dep_var_scaler',
    ...args,
  });
}
